/**
 * Distance round-trip helpers.
 *
 * The codebase stores distance as the legacy "Nmi" string (e.g. "50mi") —
 * searchApi.js and the V1 search-actions path pass it verbatim to the
 * backend, and most callers default to '50mi'. The NL pathway emits a
 * raw number from the LLM and the V2 bridge effect coerces it back. These
 * helpers centralize that round-trip so the format magic string isn't
 * duplicated in three places.
 */

export const MILES_SUFFIX = 'mi';

/**
 * Coerce a numeric or "Nmi" distance value into the canonical "Nmi"
 * string form expected by the legacy filter contract.
 *
 * @param {number|string|null|undefined} value
 * @returns {string|null} the "Nmi" string, or null if the input doesn't
 *   represent a usable distance
 */
export function formatDistanceMi(value) {
    if (value == null || value === '') {
        return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value}${MILES_SUFFIX}`;
    }
    if (typeof value === 'string') {
        // Already in "Nmi" form; pass through if the prefix parses.
        const n = parseInt(value, 10);
        if (Number.isFinite(n)) {
            return `${n}${MILES_SUFFIX}`;
        }
    }
    return null;
}

/**
 * Parse a "Nmi" string (or a bare number) into a plain number.
 *
 * @param {number|string|null|undefined} value
 * @returns {number|null} the numeric distance, or null if the input
 *   doesn't represent a usable distance
 */
export function parseDistanceMi(value) {
    if (value == null || value === '') {
        return null;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const n = parseInt(value, 10);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
